import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Users,
    MessageSquare,
    Eye,
    Activity,
    DollarSign,
    TrendingUp,
    Radio,
    Globe,
    Trophy,
    Megaphone,
    Smartphone,
    RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    AreaChart,
    Area,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
    Legend,
    LineChart,
    Line,
} from "recharts";
import { Download, FileText, ArrowUp, ArrowDown, Minus } from "lucide-react";

const PALETTE = ["#e60000", "#ff6b6b", "#ffa94d", "#ffd43b", "#40c057", "#15aabf", "#4c6ef5"];

const fmtTZS = (n) => {
    if (typeof n !== "number") return "TZS 0";
    if (n >= 1_000_000) return `TZS ${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `TZS ${(n / 1_000).toFixed(1)}K`;
    return `TZS ${Math.round(n).toLocaleString()}`;
};

const fmtNum = (n) => {
    if (typeof n !== "number") return "0";
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n.toLocaleString();
};

const StatCard = ({ icon: Icon, label, value, sub, sparkData, sparkKey, delta, color = "electric", testId }) => {
    const DeltaIcon = delta > 0 ? ArrowUp : delta < 0 ? ArrowDown : Minus;
    const deltaColor = delta > 0 ? "text-electric" : delta < 0 ? "text-red-400" : "text-white/40";
    return (
        <Card className="bg-paper border-border p-4 relative overflow-hidden group hover:border-electric/40 transition-colors" data-testid={testId}>
            <div className="absolute top-0 right-0 w-20 h-20 bg-electric/5 rounded-full blur-2xl -translate-y-6 translate-x-6 group-hover:bg-electric/10 transition-colors" />
            <div className="relative">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded bg-${color}/10`}>
                            <Icon className={`h-3.5 w-3.5 text-${color}`} />
                        </div>
                        <p className="text-white/50 text-[10px] uppercase tracking-wider font-bold">{label}</p>
                    </div>
                    {typeof delta === "number" && (
                        <div className={`flex items-center gap-0.5 text-[10px] font-bold ${deltaColor}`}>
                            <DeltaIcon className="h-3 w-3" />
                            {Math.abs(delta)}%
                        </div>
                    )}
                </div>
                <p className="text-2xl md:text-3xl font-heading font-black text-white mt-2 leading-none">{value}</p>
                {sub && <p className="text-white/40 text-[10px] mt-1">{sub}</p>}
                {sparkData && sparkData.length > 1 && (
                    <div className="h-8 mt-2 -mx-1 -mb-1">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={sparkData}>
                                <Line
                                    type="monotone"
                                    dataKey={sparkKey}
                                    stroke="#e60000"
                                    strokeWidth={2}
                                    dot={false}
                                    isAnimationActive={false}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </Card>
    );
};

const AnalyticsDashboard = () => {
    const { token } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const authH = () => ({ Authorization: `Bearer ${token}` });

    const fetch = async () => {
        setRefreshing(true);
        try {
            const r = await axios.get(`${API}/analytics/dashboard`, { headers: authH() });
            setData(r.data);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetch();
        const t = setInterval(fetch, 180_000); // auto-refresh every 3 min
        return () => clearInterval(t);
    }, []);

    if (loading || !data) {
        return (
            <AdminLayout title="Analytics">
                <div className="text-white/50 py-16 text-center">Loading analytics…</div>
            </AdminLayout>
        );
    }

    const s = data.snapshot;
    const daily = data.trends.daily;
    const sportViewsArr = Object.entries(data.content.views_by_sport).map(([name, views]) => ({ name, views }));
    const channelArr = Object.entries(data.content.views_by_channel).map(([name, v]) => ({ name, value: v }));
    const subsBySource = Object.entries(data.customers.by_source).map(([name, v]) => ({ name, value: v }));
    const smsBySource = Object.entries(data.sms.by_source).map(([name, count]) => ({ name, count }));

    // Deltas: compare latest day vs previous day in the 14-day trend
    const last = daily[daily.length - 1] || {};
    const prev = daily[daily.length - 2] || {};
    const pctDelta = (a, b) => {
        if (!b) return a ? 100 : 0;
        return Math.round(((a - b) / b) * 100);
    };
    const deltaWeb = pctDelta(last.web_views || 0, prev.web_views || 0);
    const deltaSms = pctDelta(last.sms_count || 0, prev.sms_count || 0);
    const deltaRev = pctDelta(last.total_revenue || 0, prev.total_revenue || 0);
    const deltaSubs = pctDelta(last.new_subscribers || 0, prev.new_subscribers || 0);

    // Downloads
    const openReport = async () => {
        try {
            const r = await axios.get(`${API}/analytics/report.html`, {
                headers: authH(),
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/html" }));
            window.open(url, "_blank");
        } catch {
            // ignore
        }
    };

    const downloadCsv = async () => {
        try {
            const r = await axios.get(`${API}/analytics/export.csv`, {
                headers: authH(),
                responseType: "blob",
            });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `dimbani-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            // ignore
        }
    };

    return (
        <AdminLayout title="Analytics & Reports">
            <div className="space-y-6" data-testid="analytics-dashboard">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-white flex items-center gap-2">
                            <Activity className="h-6 w-6 text-electric" />
                            Live Analytics & Revenue
                        </h1>
                        <p className="text-white/50 text-sm mt-1">
                            Auto-refreshes every 3 min · Last: {new Date(data.generated_at).toLocaleTimeString()}
                        </p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button
                            onClick={downloadCsv}
                            variant="outline"
                            className="border-electric/40 text-electric hover:bg-electric/10"
                            data-testid="export-csv-btn"
                        >
                            <Download className="h-4 w-4 mr-2" />
                            Download CSV
                        </Button>
                        <Button
                            onClick={openReport}
                            variant="outline"
                            className="border-electric/40 text-electric hover:bg-electric/10"
                            data-testid="export-pdf-btn"
                        >
                            <FileText className="h-4 w-4 mr-2" />
                            Open Report (PDF)
                        </Button>
                        <Button
                            onClick={fetch}
                            disabled={refreshing}
                            className="bg-electric hover:bg-vodaredDark text-white"
                            data-testid="refresh-btn"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                    </div>
                </div>

                {/* Snapshot - Quick Snippets */}
                <div>
                    <h2 className="text-white/60 text-xs uppercase tracking-widest mb-3">Now · Today · Revenue</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <StatCard icon={Radio} label="Active Now" value={s.active_now} sub="last 5 min" testId="stat-active-now" />
                        <StatCard icon={Users} label="Active Today" value={fmtNum(s.active_today)} sub="unique visitors" testId="stat-active-today" />
                        <StatCard
                            icon={Eye}
                            label="Web Views"
                            value={fmtNum(s.web_views_today)}
                            sub="today"
                            delta={deltaWeb}
                            sparkData={daily}
                            sparkKey="web_views"
                            testId="stat-web-views"
                        />
                        <StatCard
                            icon={MessageSquare}
                            label="SMS Today"
                            value={fmtNum(s.sms_count_today)}
                            sub="inquiries & replies"
                            delta={deltaSms}
                            sparkData={daily}
                            sparkKey="sms_count"
                            testId="stat-sms"
                        />
                        <StatCard
                            icon={Users}
                            label="Active Subs"
                            value={fmtNum(s.subscriptions_active)}
                            sub="SMS + Web"
                            delta={deltaSubs}
                            sparkData={daily}
                            sparkKey="new_subscribers"
                            testId="stat-subs"
                        />
                        <StatCard
                            icon={DollarSign}
                            label="Revenue Today"
                            value={fmtTZS(s.revenue_today.total)}
                            sub={`Web ${fmtTZS(s.revenue_today.web)} · SMS ${fmtTZS(s.revenue_today.sms)}`}
                            delta={deltaRev}
                            sparkData={daily}
                            sparkKey="total_revenue"
                            testId="stat-revenue"
                        />
                    </div>
                </div>

                {/* SMS Engagement Metrics — explicit row */}
                <div>
                    <h2 className="text-white/60 text-xs uppercase tracking-widest mb-3">SMS Engagement</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatCard
                            icon={Smartphone}
                            label="SMS Customers"
                            value={fmtNum(data.sms?.subscribers_total || 0)}
                            sub={`${fmtNum(data.sms?.subscribers_active || 0)} active`}
                            color="electric"
                            testId="stat-sms-customers"
                        />
                        <StatCard
                            icon={MessageSquare}
                            label="SMS Interactions"
                            value={fmtNum(data.sms?.interactions_total || 0)}
                            sub={`${fmtNum(data.sms?.interactions_14d || 0)} in last 14d`}
                            color="electric"
                            testId="stat-sms-interactions"
                        />
                        <StatCard
                            icon={Megaphone}
                            label="Auto-Pushed SMS"
                            value={fmtNum(data.sms?.auto_pushed_total || 0)}
                            sub={`${fmtNum(data.sms?.campaigns_sent_today || 0)} sent today`}
                            color="electric"
                            testId="stat-sms-auto"
                        />
                        <StatCard
                            icon={Activity}
                            label="Total Campaigns"
                            value={fmtNum(data.sms?.total_campaigns || 0)}
                            sub="lifetime"
                            color="electric"
                            testId="stat-sms-campaigns-total"
                        />
                    </div>
                </div>

                {/* Revenue & Traffic Trend */}
                <Card className="bg-paper border-border p-5" data-testid="trend-chart">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-electric" />
                            14-Day Trend — Traffic & Revenue
                        </h3>
                        <div className="flex items-center gap-3 text-xs">
                            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-electric" /><span className="text-white/60">Web Rev</span></div>
                            <div className="flex items-center gap-1"><div className="w-3 h-3 rounded-sm bg-orange-400" /><span className="text-white/60">SMS Rev</span></div>
                        </div>
                    </div>
                    <ResponsiveContainer width="100%" height={260}>
                        <AreaChart data={daily} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="webG" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#e60000" stopOpacity={0.6} />
                                    <stop offset="100%" stopColor="#e60000" stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="smsG" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#ffa94d" stopOpacity={0.6} />
                                    <stop offset="100%" stopColor="#ffa94d" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                            <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                            <YAxis stroke="#666" tick={{ fontSize: 11 }} />
                            <Tooltip
                                contentStyle={{ background: "#151515", border: "1px solid #2a2a2a", borderRadius: 4 }}
                                labelStyle={{ color: "#fff" }}
                                formatter={(v, n) => [fmtTZS(v), n.replace("_", " ")]}
                            />
                            <Area type="monotone" dataKey="web_revenue" stroke="#e60000" fill="url(#webG)" strokeWidth={2} />
                            <Area type="monotone" dataKey="sms_revenue" stroke="#ffa94d" fill="url(#smsG)" strokeWidth={2} />
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                {/* Customer Trend + Engagement */}
                <div className="grid lg:grid-cols-2 gap-5">
                    <Card className="bg-paper border-border p-5" data-testid="subscribers-chart">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                            <Users className="h-4 w-4 text-electric" />
                            New Subscribers (14d)
                        </h3>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                                <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: "#151515", border: "1px solid #2a2a2a", borderRadius: 4 }} />
                                <Bar dataKey="new_subscribers" fill="#e60000" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>

                    <Card className="bg-paper border-border p-5" data-testid="sms-chart">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                            <MessageSquare className="h-4 w-4 text-electric" />
                            SMS Activity (14d)
                        </h3>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={daily}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                <XAxis dataKey="date" stroke="#666" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                                <YAxis stroke="#666" tick={{ fontSize: 10 }} />
                                <Tooltip contentStyle={{ background: "#151515", border: "1px solid #2a2a2a", borderRadius: 4 }} />
                                <Legend wrapperStyle={{ fontSize: 11, color: "#888" }} />
                                <Bar dataKey="sms_count" name="Inquiries" stackId="a" fill="#ffa94d" radius={[0, 0, 0, 0]} />
                                <Bar dataKey="campaigns_sent" name="Campaign Pushes" stackId="a" fill="#e60000" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </div>

                {/* Content + Channels + SMS sources */}
                <div className="grid lg:grid-cols-3 gap-5">
                    {/* Top consumed content */}
                    <Card className="bg-paper border-border p-5 lg:col-span-2" data-testid="top-content-card">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                            <Trophy className="h-4 w-4 text-electric" />
                            Most Consumed Content
                        </h3>
                        <div className="space-y-2">
                            {data.content.top_articles.length === 0 ? (
                                <p className="text-white/40 text-sm py-6 text-center">No content views yet.</p>
                            ) : (
                                data.content.top_articles.map((a, i) => (
                                    <div key={a.id} className="flex items-center gap-3 p-2 rounded hover:bg-subtle/50" data-testid={`top-content-${i}`}>
                                        <span className="text-electric font-heading font-black w-6 text-right">{i + 1}.</span>
                                        {a.image_url ? (
                                            <img src={a.image_url} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                                        ) : (
                                            <div className="w-12 h-12 rounded bg-subtle shrink-0" />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white text-sm font-medium truncate">{a.title_sw || a.title}</div>
                                            <div className="text-white/40 text-xs">{a.sport || "—"}</div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-electric font-heading font-bold">{fmtNum(a.total_views)}</div>
                                            <div className="text-white/40 text-[10px] uppercase">views</div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </Card>

                    {/* Channel mix */}
                    <Card className="bg-paper border-border p-5" data-testid="channel-mix-card">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                            <Globe className="h-4 w-4 text-electric" />
                            Content Channel Mix
                        </h3>
                        <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                                <Pie data={channelArr} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70}>
                                    {channelArr.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#151515", border: "1px solid #2a2a2a", borderRadius: 4 }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-1 text-xs">
                            {channelArr.map((c, i) => (
                                <div key={c.name} className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                                        <span className="text-white/70 capitalize">{c.name}</span>
                                    </span>
                                    <span className="text-white font-mono">{fmtNum(c.value)}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* Most Visited Pages */}
                <Card className="bg-paper border-border p-5" data-testid="most-visited-pages-card">
                    <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                        <Globe className="h-4 w-4 text-electric" />
                        Most Visited Pages (last 14 days)
                    </h3>
                    {(data.pages?.most_visited || []).length === 0 ? (
                        <p className="text-white/40 text-sm py-6 text-center">Hakuna data ya kutosha bado.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-white/40 text-[10px] uppercase tracking-wider border-b border-border">
                                        <th className="text-left py-2 font-medium">#</th>
                                        <th className="text-left py-2 font-medium">Path</th>
                                        <th className="text-right py-2 font-medium">Views</th>
                                        <th className="text-right py-2 font-medium">Unique Visitors</th>
                                        <th className="text-right py-2 font-medium pl-4">% of Top</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.pages?.most_visited || []).map((p, i, arr) => {
                                        const max = arr[0]?.views || 1;
                                        const pct = Math.round((p.views / max) * 100);
                                        return (
                                            <tr key={p.path} className="border-b border-border/40 hover:bg-subtle/30" data-testid={`page-row-${i}`}>
                                                <td className="py-2 text-white/40 font-heading font-bold">{i + 1}</td>
                                                <td className="py-2 text-white font-mono text-xs truncate max-w-xs">{p.path}</td>
                                                <td className="py-2 text-right text-electric font-bold">{fmtNum(p.views)}</td>
                                                <td className="py-2 text-right text-white/70">{fmtNum(p.unique_visitors)}</td>
                                                <td className="py-2 pl-4 w-32">
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex-1 bg-subtle h-2 rounded-full overflow-hidden">
                                                            <div className="bg-electric h-full" style={{ width: `${pct}%` }} />
                                                        </div>
                                                        <span className="text-white/40 text-[10px] w-7 text-right">{pct}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                {/* Top teams + Subscriber sources + SMS source mix */}
                <div className="grid lg:grid-cols-3 gap-5">
                    <Card className="bg-paper border-border p-5" data-testid="top-teams-card">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                            <Trophy className="h-4 w-4 text-electric" />
                            Top Followed Teams
                        </h3>
                        {data.customers.top_teams.length === 0 ? (
                            <p className="text-white/40 text-sm py-6 text-center">No team subscribers yet.</p>
                        ) : (
                            <div className="space-y-2">
                                {data.customers.top_teams.map((t, i) => {
                                    const max = data.customers.top_teams[0]?.count || 1;
                                    return (
                                        <div key={t.team} data-testid={`team-row-${i}`}>
                                            <div className="flex items-center justify-between text-xs mb-1">
                                                <span className="text-white/80">{i + 1}. {t.team}</span>
                                                <span className="text-electric font-mono font-bold">{t.count}</span>
                                            </div>
                                            <div className="h-1.5 bg-subtle rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-electric transition-all"
                                                    style={{ width: `${(t.count / max) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </Card>

                    <Card className="bg-paper border-border p-5" data-testid="subs-source-card">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                            <Users className="h-4 w-4 text-electric" />
                            Subscribers by Source
                        </h3>
                        <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                                <Pie data={subsBySource} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70}>
                                    {subsBySource.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#151515", border: "1px solid #2a2a2a", borderRadius: 4 }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <div className="space-y-1 text-xs">
                            {subsBySource.map((c, i) => (
                                <div key={c.name} className="flex items-center justify-between">
                                    <span className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full" style={{ background: PALETTE[i % PALETTE.length] }} />
                                        <span className="text-white/70 capitalize">{c.name}</span>
                                    </span>
                                    <span className="text-white font-mono">{c.value}</span>
                                </div>
                            ))}
                        </div>
                    </Card>

                    <Card className="bg-paper border-border p-5" data-testid="sms-source-card">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                            <Smartphone className="h-4 w-4 text-electric" />
                            SMS by Command Type
                        </h3>
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={smsBySource} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                                <XAxis type="number" stroke="#666" tick={{ fontSize: 10 }} />
                                <YAxis dataKey="name" type="category" stroke="#666" tick={{ fontSize: 10 }} width={80} />
                                <Tooltip contentStyle={{ background: "#151515", border: "1px solid #2a2a2a", borderRadius: 4 }} />
                                <Bar dataKey="count" fill="#e60000" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>
                </div>

                {/* Sport views */}
                <Card className="bg-paper border-border p-5" data-testid="sport-views-card">
                    <h3 className="text-white font-heading font-bold flex items-center gap-2 mb-3">
                        <TrendingUp className="h-4 w-4 text-electric" />
                        Views by Sport
                    </h3>
                    <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={sportViewsArr}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                            <XAxis dataKey="name" stroke="#666" tick={{ fontSize: 11 }} />
                            <YAxis stroke="#666" tick={{ fontSize: 11 }} />
                            <Tooltip contentStyle={{ background: "#151515", border: "1px solid #2a2a2a", borderRadius: 4 }} />
                            <Bar dataKey="views" fill="#e60000" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                {/* Revenue breakdown 14d */}
                <Card className="bg-gradient-to-br from-electric/10 to-paper border-electric/30 p-5" data-testid="revenue-summary">
                    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                        <h3 className="text-white font-heading font-bold flex items-center gap-2">
                            <DollarSign className="h-5 w-5 text-electric" />
                            14-Day Revenue Summary
                        </h3>
                        <Badge className="bg-electric/20 text-electric border-0">
                            Total: {fmtTZS(data.revenue["14d_total"].total)}
                        </Badge>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-subtle rounded p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Globe className="h-4 w-4 text-electric" />
                                <p className="text-white/50 text-xs uppercase tracking-wider">Web</p>
                            </div>
                            <p className="text-2xl font-heading font-black text-white">{fmtTZS(data.revenue["14d_total"].web)}</p>
                            <p className="text-white/40 text-xs mt-1">@ TZS {data.revenue.rates.web_impression}/impression</p>
                        </div>
                        <div className="bg-subtle rounded p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Megaphone className="h-4 w-4 text-electric" />
                                <p className="text-white/50 text-xs uppercase tracking-wider">SMS</p>
                            </div>
                            <p className="text-2xl font-heading font-black text-white">{fmtTZS(data.revenue["14d_total"].sms)}</p>
                            <p className="text-white/40 text-xs mt-1">@ TZS {data.revenue.rates.sms_inquiry}/reply · TZS {data.revenue.rates.sms_push}/push</p>
                        </div>
                        <div className="bg-subtle rounded p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Users className="h-4 w-4 text-electric" />
                                <p className="text-white/50 text-xs uppercase tracking-wider">Subscriptions</p>
                            </div>
                            <p className="text-2xl font-heading font-black text-white">{fmtTZS(data.revenue["14d_total"].subscriptions)}</p>
                            <p className="text-white/40 text-xs mt-1">@ TZS {data.revenue.rates.subscription_monthly}/mo · {data.customers.active} active</p>
                        </div>
                    </div>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default AnalyticsDashboard;
